import { combineReducers } from 'redux';

import { META_SOURCE } from 'constants/ChecklistConstants';
import * as types from 'modules/checklists/actionTypes';
import type { DeleteChecklistSuccessAction, SearchChecklistsSuccessAction } from 'modules/checklists/types';
import type { Action } from 'modules/global/generic.types';
import apiError from 'modules/global/reducers/generic.error';
import loading from 'modules/global/reducers/generic.loading';
import { initialState } from 'modules/global/reducers/generic.page';
import selectSlice from 'modules/global/reducers/generic.selectSlice';
import loadingArraySlice from 'modules/global/reducers/loadingArraySlice';
import type { ReducerFunction } from 'modules/global/reducers/reducers.types';
import createSearchReducers from 'modules/global/reducers/simpleSearchReducer';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { FETCH_MODEL_SUCCESS } from 'modules/selfServices/actionTypes';
import type { GetModelSuccessAction } from 'modules/selfServices/types';
import { uniq } from 'utils/ObjectUtils';

import dashboard from './dashboard';
import defaultSlice from './default';
import details from './details';
import duplicate from './duplicate';
import stack from './stack';

const isExtrasDashboard = ({ meta }: Action) => meta?.source === META_SOURCE.EXTRAS_DASHBOARD;

const setExtrasIds = (state: string[], action: SearchChecklistsSuccessAction): string[] =>
  (isExtrasDashboard(action) ? action.payload.content.map(({ id }) => id) : state);

const deleteOne = (state: string[], { payload: deletedId }: DeleteChecklistSuccessAction): string[] =>
  state.filter(id => id !== deletedId);

const afterFetchModel = (state: string[], { payload: model }: GetModelSuccessAction): string[] =>
  (model && 'checklists' in model && model.checklists.length > 0
    ? uniq([...state, ...model.checklists.map(({ id }) => id)])
    : state);

const ADVICE_SLICE_CONFIG = {
  global: {
    selectId: (_: unknown, meta: { adviceId?: unknown }) => meta.adviceId as string,
  },
};

const ui = combineReducers({
  stack, // Define if a checklist stack is opened or not
  details,
  dashboard,
  duplicate,
  defaultChecklists: createSearchReducers(types.FETCH_SELECTED_CHECKLISTS_REQUEST, {
    global: { matcher: ({ meta }) => meta?.source === META_SOURCE.DEFAULT_CHECKLISTS },
  }),
  default: defaultSlice,
  create: combineReducers({
    isFetching: loading(types.CREATE_REQUEST),
    apiError: apiError(types.CREATE_REQUEST),
  }),
  patchingAdviceState: loadingArraySlice(types.PATCH_ADVICE_STATE, ADVICE_SLICE_CONFIG),
  extras: createSearchReducers(types.SEARCH_CHECKLISTS_REQUEST, {
    ids: {
      [types.SEARCH_CHECKLISTS_SUCCESS]: setExtrasIds as ReducerFunction<string[]>,
      [types.DELETE_CHECKLIST_SUCCESS]: deleteOne as ReducerFunction<string[]>,
    },
    global: { matcher: isExtrasDashboard },
  }),
  extrasStats: createSearchReducers(types.SEARCH_CHECKLISTS_REQUEST, {
    global: { matcher: ({ meta }) => meta?.source === META_SOURCE.EXTRAS_STATS },
  }),
  selectExtrasIntervention: createSearchReducers(types.SEARCH_CHECKLISTS_REQUEST, {
    ids: {
      [LOCATION_CHANGE]: () => [],
      [FETCH_MODEL_SUCCESS]: afterFetchModel as ReducerFunction<string[]>,
      [types.SEARCH_CHECKLISTS_REQUEST]: (state: string[]) => state, // override default behavior that reset the state
    },
    page: {
      [LOCATION_CHANGE]: () => initialState,
    },
    global: {
      matcher: ({ meta }) => meta?.source === META_SOURCE.EXTRAS_INTERVENTION_DETAILS,
    },
  }),
  newSelect: selectSlice(types.FETCH_CHECKLISTS_SELECT_REQUEST),
});

export type ChecklistUiReducer = ReturnType<typeof ui>;

export default ui;
